/*
 * Copyright (c) 2021 biocrates life sciences ag, All rights reserved.
 */

import React, {useEffect, useState} from "react"

import {useDispatch} from "react-redux"

import {
    Callout,
    Classes,
    Dialog,
    Intent,
    Switch,
    TextArea,
    Button,
} from "@blueprintjs/core"
import axios from "axios"
import * as htmlToImage from "html-to-image"

import env from "../../context/Env"
import Error from "../../models/error"
import Issue from "../../models/issue"
import dataURItoBlob from "../../utils/fileUtils"
import handleBooleanChange from "../../utils/handleChangehelpers"
import ActionButton from "../buttons/ActionButton"
import MinimalButton from "../buttons/MinimalButton"
import CenteredLoading from "../center-loading/CenteredLoading"
import DropZone from "../drop-zone/DropZone"
import "./ErrorReport.scss"
import {showToast} from "../toast/AppToaster"
import getHandleDroppedItems from "../drop-zone/dropZoneUtils"

export interface ErrorReportProps {
    isOpen: boolean
    error: Error | undefined
    onClose: () => void
}

export default function ErrorReport({
    error,
    onClose,
    isOpen,
}: ErrorReportProps) {
    const [issue, setIssue] = useState<Issue>({
        projectShortName: env("YOUTRACK_PROJECT_SHORT_NAME"),
        files: [],
    })
    const dispatch = useDispatch()

    const [image, setImage] = useState("")
    const [
        showInvalidFileTypeWarning,
        setShowInvalidFileTypeWarning,
    ] = useState<boolean>(false)

    useEffect(() => {
        if (isOpen) {
            setIssue({
                ...issue,
                files: [],
                description: "",
                summary: error?.message,
                stacktrace: error?.stacktrace,
            })
            htmlToImage
                .toJpeg(document.body, {backgroundColor: "white", height: 800})
                .then(function (dataUrl) {
                    setImage(dataUrl)
                })
                .catch(function (errore) {
                    console.error("oops, something went wrong!", errore)
                })
        }
    }, [isOpen])

    const sendIssue = (reportedIssue: Issue) => {
        const formPayload = new FormData()

        if (reportedIssue.files) {
            reportedIssue.files.forEach((file) => {
                formPayload.append("files", file)
            })
        }

        if (reportedIssue.screenshot) {
            formPayload.append(
                "screenshot",
                dataURItoBlob(reportedIssue.screenshot),
                "screenshot"
            )
        }

        formPayload.append(
            "issue",
            new Blob(
                [
                    JSON.stringify({
                        ...reportedIssue,
                        files: undefined,
                        screenshot: undefined,
                    }),
                ],
                {
                    type: "application/json",
                }
            )
        )

        axios
            .post(`${env("REPORT_SPACE_URL")}/api/issue`, formPayload)
            .catch((err) => {
                showToast({
                    type: "warning",
                    message:
                        "Sending error report failure, please contact your administrator.",
                    dispatch,
                })
                console.log(err)
            })
    }

    const handleSubmit = () => {
        sendIssue(issue)
        onClose()
    }

    const handleClose = () => {
        onClose()
    }

    const handleData = (e: any) => {
        setIssue({
            ...issue,
            [e.currentTarget.id]: e.currentTarget.value,
        })
    }

    const addFile = (file: File) => {
        setIssue((prevState) => {
            const files = [...prevState.files]
            files.push(file)
            return {...prevState, files}
        })
    }

    const rejectFile = (file: File) => {
        setShowInvalidFileTypeWarning(true)
    }

    return (
        <Dialog
            title="Error Report"
            className="scrollable-dialog"
            isOpen={isOpen}
            onClose={() => handleClose()}
        >
            <div className={`${Classes.DIALOG_BODY} scrollable-dialog-body`}>
                <h3 style={{marginTop: "0px"}}>{issue.summary}</h3>
                <div className="scrollableContent">
                    <p>
                        Description:
                        <TextArea
                            id="description"
                            intent={Intent.PRIMARY}
                            style={{border: 0}}
                            growVertically
                            large
                            className="bp4-fill"
                            onChange={handleData}
                            value={issue.description}
                        />
                    </p>
                    <Callout intent={Intent.NONE}>
                        <Switch
                            type="checkbox"
                            id="enabled"
                            label="Add screenshot:"
                            defaultChecked={false}
                            onChange={handleBooleanChange((value) => {
                                if (value) {
                                    issue.screenshot = image
                                } else {
                                    issue.screenshot = undefined
                                }
                            })}
                        />
                        <div>
                            {image ? (
                                <img
                                    src={image}
                                    style={{
                                        borderStyle: "solid",
                                        borderWidth: "1px",
                                        width: "100%",
                                    }}
                                    alt="Screenshot"
                                />
                            ) : (
                                <div>
                                    <CenteredLoading />
                                </div>
                            )}
                        </div>
                    </Callout>

                    <Callout>
                        <div className="dropzoneContainer">
                            <DropZone
                                text="Drop files here"
                                handleDroppedItems={getHandleDroppedItems(
                                    [
                                        "image/bmp",
                                        "image/png",
                                        "image/jpeg",
                                        "image/gif",
                                        "application/pdf",
                                    ],
                                    addFile,
                                    rejectFile
                                )}
                            />
                            {showInvalidFileTypeWarning && (
                                <p className="warningText">
                                    Only bmp, gif, jpeg, png and pdf files are
                                    allowed!
                                </p>
                            )}
                        </div>

                        <ul>
                            {issue.files.map((f: File, i) => (
                                <li key={f.name}>
                                    {f.name}{" "}
                                    <MinimalButton
                                        icon="cross"
                                        onClick={() => {
                                            issue.files.splice(
                                                issue.files.indexOf(f),
                                                1
                                            )
                                            setIssue({
                                                ...issue,
                                                files: issue.files,
                                            })
                                        }}
                                    />
                                </li>
                            ))}
                        </ul>
                    </Callout>
                </div>
            </div>
            <div
                className={Classes.DIALOG_FOOTER}
                style={{display: "flex", justifyContent: "space-around"}}
            >
                <Button
                    minimal
                    text="Don't Send"
                    onClick={() => handleClose()}
                />
                <ActionButton
                    text="Send Error Report"
                    onClick={() => handleSubmit()}
                />
            </div>
        </Dialog>
    )
}
